import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import "../../assets/css/Text-General.css";

interface FormattedTextProps {
  text: string | null | undefined;
  defaultExpanded?: boolean;
}

export const FormattedText = ({ text, defaultExpanded = false }: FormattedTextProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!text) return <Typography>-</Typography>;

  const formatText = (content: string) => {
    const lines = content.split('\n');
    const formattedElements: React.ReactElement[] = [];
    let currentParagraph: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        formattedElements.push(
          <Typography key={`para-${formattedElements.length}`} sx={{ mb: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {currentParagraph.join(' ')}
          </Typography>
        );
        currentParagraph = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Empty line - end current paragraph
      if (trimmedLine === '') {
        flushParagraph();
        return;
      }

      // Section header detection - common headers in email summaries
      const commonHeaders = ['Proposal', 'Background', 'Asset', 'Assets', 'Market', 'Underwriting', 'Summary'];
      const isHeader = commonHeaders.some(header => 
        trimmedLine === header || 
        trimmedLine.startsWith(header + ' •') ||
        trimmedLine.startsWith(header + ' ')
      ) && trimmedLine.length < 60;
      
      // Section header (matches common headers or short line after empty line)
      if (
        isHeader ||
        (
          trimmedLine.length < 50 && 
          !trimmedLine.startsWith('•') && 
          !trimmedLine.startsWith('–') &&
          !trimmedLine.startsWith('-') &&
          !trimmedLine.includes(':') &&
          (index === 0 || lines[index - 1]?.trim() === '')
        )
      ) {
        flushParagraph();
        // Remove any trailing bullet from header
        const headerText = trimmedLine.replace(/ •$/, '').trim();
        formattedElements.push(
          <Typography 
            key={`header-${index}`} 
            sx={{ 
              fontWeight: 700, 
              fontSize: '1.1rem', 
              mt: index > 0 ? 3 : 0, 
              mb: 1.5,
              color: '#1976d2',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {headerText}
          </Typography>
        );
        return;
      }

      // Bullet point (starts with •)
      if (trimmedLine.startsWith('•')) {
        flushParagraph();
        const bulletText = trimmedLine.substring(1).trim();
        formattedElements.push(
          <Box key={`bullet-${index}`} sx={{ display: 'flex', mb: 0.75, pl: 2, minWidth: 0 }}>
            <Typography component="span" sx={{ mr: 1, flexShrink: 0 }}>•</Typography>
            <Typography sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0 }}>{bulletText}</Typography>
          </Box>
        );
        return;
      }

      // Sub-bullet (dash followed by whitespace, so "-2.5%" keeps its sign)
      const subBulletMatch = /^[-–]\s+/.exec(trimmedLine);
      if (subBulletMatch) {
        flushParagraph();
        const subBulletText = trimmedLine.substring(subBulletMatch[0].length);
        formattedElements.push(
          <Box key={`subbullet-${index}`} sx={{ display: 'flex', mb: 0.5, pl: 4, minWidth: 0 }}>
            <Typography component="span" sx={{ mr: 1, flexShrink: 0 }}>–</Typography>
            <Typography sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0 }}>{subBulletText}</Typography>
          </Box>
        );
        return;
      }

      // Regular paragraph text
      currentParagraph.push(trimmedLine);
    });

    // Flush any remaining paragraph
    flushParagraph();

    return formattedElements;
  };

  const formattedContent = formatText(text);
  const isLongText = text.length > 500; // Consider it long if over 500 characters

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      {isLongText && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{ p: 0.5 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography 
            variant="body2" 
            sx={{ 
              cursor: 'pointer', 
              color: 'text.secondary',
              ml: 0.5,
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Typography>
        </Box>
      )}
      <Collapse in={expanded || !isLongText}>
        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {formattedContent}
        </Box>
      </Collapse>
    </Box>
  );
};

